import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Backdrop,
  Chip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store';
import { closeChatSidebar } from '../../store/slices/uiSlice';
import { sendChatMessage, addMessage } from '../../store/slices/chatSlice';
import { useScenarioStream } from '../../hooks/useScenarioStream';
import { ChatMessage, ChatResponse } from '../../types';

const SIDEBAR_WIDTH = 420;

interface MessageBubbleProps {
  message: ChatMessage;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '16px',
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          padding: '12px 16px',
          borderRadius: '16px',
          fontSize: '13px',
          lineHeight: 1.5,
          ...(isUser
            ? {
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: '#fff',
              borderBottomRightRadius: '4px',
            }
            : {
              backgroundColor: '#ffffff',
              color: '#2d3436',
              border: '1px solid #e0e0e0',
              borderBottomLeftRadius: '4px',
            }),
        }}
      >
        {message.content}
      </Box>
    </Box>
  );
};


const ChatSidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { runAnalysis } = useScenarioStream();
  const isOpen = useAppSelector((state) => state.ui.chatSidebarOpen);
  const { messages, suggestedQuestions, chatBrief, isLoading, pendingPrompt } = useAppSelector((state) => state.chat);
  const { result, pendingScenarioId } = useAppSelector((state) => state.analysis);
  const effectiveSuggestions = (result?.suggested_questions && result.suggested_questions.length > 0)
    ? result.suggested_questions
    : suggestedQuestions;
  const effectiveBrief = chatBrief || result?.chat_brief;

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingPrompt]);

  const handleClose = () => {
    dispatch(closeChatSidebar());
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const getChatContext = (): string => {
    if (!result) return '';

    const parts: string[] = [];

    if (result.scenario_id) {
      parts.push(`Current scenario: ${result.scenario_id}`);
    }

    if (result.deficit_allocation_plan) {
      const dp = result.deficit_allocation_plan;
      parts.push(
        `Deficit allocation: ${dp.total_supply} supply vs ${dp.total_demand} demand (${Math.round(dp.fulfillment_rate * 100)}% fulfilled)`
      );
    }

    if (result.executive_summary) {
      parts.push(`Summary: ${result.executive_summary}`);
    }

    return parts.join('\n');
  };

  const submitPrompt = async (prompt: string) => {
    const trimmedInput = prompt.trim();
    if (!trimmedInput || isLoading) return;

    dispatch(
      addMessage({
        role: 'user',
        content: trimmedInput,
        timestamp: new Date().toISOString(),
      })
    );

    if (prompt === inputValue) {
      setInputValue('');
    }

    const resultAction = await dispatch(
      sendChatMessage({
        message: trimmedInput,
        context: getChatContext(),
        pendingScenarioId: pendingScenarioId || result?.scenario_id || undefined,
      })
    );

    if (sendChatMessage.fulfilled.match(resultAction)) {
      const response = resultAction.payload;

      if (response.whatif_weights || response.global_supply_override || response.global_demand_override || response.parameter_overrides || response.regional_overrides || response.batch_overrides) {
        // Trigger what-if analysis
        const currentId = result?.scenario_id || response.pending_scenario_id;
        if (currentId) {
          // Preserve current weights if not explicitly changed
          const effectiveWeights = response.whatif_weights || result?.weights || null;
          runAnalysis(
            currentId,
            effectiveWeights,
            response.global_supply_override || null,
            response.global_demand_override || null,
            false,
            response.parameter_overrides || null,
            response.regional_overrides || null,
            result?.selected_strategy,
            response.batch_overrides || null
          );
        }
      } else if (response.should_run) {
        // Trigger analysis for the specific scenario
        runAnalysis(response.should_run);
      }
    }
  };

  const handleSend = async () => {
    await submitPrompt(inputValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Backdrop
        open={isOpen}
        onClick={handleClose}
        sx={{
          zIndex: 998,
          backgroundColor: 'rgba(0,0,0,0.3)',
        }}
      />
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            zIndex: 999,
          },
        }}
      >
        <Box
          sx={{
            padding: '18px 20px',
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ChatIcon />
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
              Chat Assistant
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '8px',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            <CloseIcon sx={{ color: '#fff', fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              padding: '14px 20px',
              fontSize: '13px',
              color: 'text.secondary',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
            }}
          >
            <Typography component="span" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              Ask about:
            </Typography>{' '}
            deviations, allocations, cost breakdowns, or what-if scenarios
          </Box>

          <Box
            sx={{
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              backgroundColor: '#f8f9fa',
            }}
          >
            {messages.length === 0 && !pendingPrompt && (
              <MessageBubble
                message={{
                  role: 'assistant',
                  content: effectiveBrief ||
                    "Hello! I'm your AI assistant for the Agentic Arbitrator. I can help you understand the allocation decisions, explore what-if scenarios, or dive into specific regional data. What would you like to know?",
                }}
              />
            )}

            {messages.map((msg, index) => (
              <MessageBubble key={index} message={msg} />
            ))}

            {(pendingPrompt || isLoading) && (
              <>
                {pendingPrompt && (
                  <MessageBubble
                    message={{
                      role: 'user',
                      content: pendingPrompt,
                    }}
                  />
                )}
                <MessageBubble
                  message={{
                    role: 'assistant',
                    content: '⏳ Thinking...',
                  }}
                />
              </>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Box
            sx={{
              padding: '16px 20px',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#ffffff',
            }}
          >
            {effectiveSuggestions.length > 0 && (
              <Box
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {effectiveSuggestions.map((question, idx) => (
                  <Chip
                    key={`${idx}-${question}`}
                    label={question}
                    size="small"
                    clickable
                    disabled={isLoading}
                    onClick={() => submitPrompt(question)}
                    sx={{
                      maxWidth: '100%',
                      minHeight: 30,
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      bgcolor: '#f8fafc',
                      '& .MuiChip-label': {
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: 1.35,
                        textTransform: 'none',
                        whiteSpace: 'normal',
                        display: 'block',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: '10px' }}>
              <TextField
                fullWidth
                placeholder="Ask a question..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: 'secondary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'secondary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '14px 18px',
                    fontSize: '13px',
                  },
                }}
              />
              <IconButton
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                sx={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  borderRadius: '12px',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(255,152,0,0.35)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                <SendIcon sx={{ color: '#fff', fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default ChatSidebar;
